import React, { useState, useEffect, useRef } from 'react';
import {
  Briefcase,
  Bell,
  MessageSquare,
  Menu,
  X,
  ChevronDown,
  ShieldCheck,
  User,
  LogOut,
  Building,
  PlusCircle,
  HelpCircle,
  PhoneCall,
  Lock,
} from 'lucide-react';
import { useApp, AppView } from '../../context/AppContext';
import { JobMatchLogo } from '../common/JobMatchLogo';
import { ThemeToggle } from '../common/ThemeToggle';
import { InstallAppButton } from '../pwa/PWAInstallManager';

export const Navbar: React.FC = () => {
  const {
    currentView,
    navigateTo,
    currentUser,
    unreadNotifsCount,
    logoutUser,
    openAuthModal,
    switchActiveRole,
  } = useApp();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click or escape
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsRoleDropdownOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsRoleDropdownOpen(false);
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleNavClick = (view: AppView) => {
    setIsMobileMenuOpen(false);
    setIsRoleDropdownOpen(false);
    navigateTo(view);
  };

  const isActive = (view: AppView) => currentView === view;

  return (
    <header className="sticky top-0 z-40 w-full transition-colors duration-200">
      {/* 1. TOP UTILITY STRIP - Trust, Nigeria Verification & Escrow Notice */}
      <div className="bg-[#0B1728] dark:bg-[#060D17] text-slate-300 text-[10px] sm:text-[11px] py-1.5 px-3 sm:px-6 lg:px-8 border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
          {/* Left: Brand Trust Badges */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-blue-600/90 text-white shadow-xs truncate">
              <span>🇳🇬</span>
              <span className="truncate">Nigeria's Verified Marketplace</span>
            </span>
            <span className="hidden md:inline text-slate-400 text-[11px]">
              NIN-Verified Artisans · 774 LGAs Nationwide
            </span>
          </div>

          {/* Right: Security & Escrow Indicator */}
          <div className="flex items-center gap-2 sm:gap-4 shrink-0 text-slate-400 text-[10px] sm:text-[11px]">
            <span className="inline-flex items-center gap-1 text-slate-300">
              <Lock className="w-3 h-3 text-emerald-400 shrink-0" />
              <span className="font-medium">Escrow Protected</span>
            </span>

            {/* Authenticated user role pill */}
            {currentUser && (
              <div className="flex items-center gap-1.5 border-l border-slate-700/60 pl-2 sm:pl-3">
                <span className="hidden sm:inline text-slate-400 text-[10px] uppercase tracking-wider font-semibold">
                  Role:
                </span>
                <span className="px-1.5 sm:px-2 py-0.5 bg-slate-800 text-blue-300 rounded font-bold text-[10px]">
                  {currentUser.role === 'admin'
                    ? '🛡️ Admin'
                    : currentUser.role === 'professional'
                    ? '🛠️ Pro'
                    : '👤 Customer'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2. MAIN HEADER NAVIGATION BAR */}
      <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200/90 dark:border-slate-800 transition-colors">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2 sm:gap-4">
            {/* BRAND LOGO: Clean Horizontal Lockup */}
            <a
              href="/"
              id="brand-logo-btn"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick('home');
              }}
              className="flex items-center cursor-pointer shrink-0 py-1 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
              aria-label="JobMatch NG Homepage"
            >
              <JobMatchLogo variant="horizontal" size="md" showTagline={true} />
            </a>

            {/* DESKTOP PRIMARY NAVIGATION LINKS */}
            <nav
              aria-label="Primary"
              className="hidden lg:flex items-center gap-6 xl:gap-8 text-sm font-semibold text-slate-600 dark:text-slate-300"
            >
              <a
                href="/find-a-professional"
                id="nav-find-pro-btn"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('find_pro');
                }}
                className={`py-1.5 transition-colors relative cursor-pointer ${
                  isActive('find_pro')
                    ? 'text-blue-600 dark:text-blue-400 font-bold after:absolute after:bottom-[-20px] after:left-0 after:right-0 after:h-0.5 after:bg-blue-600 dark:after:bg-blue-400'
                    : 'hover:text-blue-600 dark:hover:text-blue-400'
                }`}
              >
                Find a Professional
              </a>

              <a
                href="/how-it-works"
                id="nav-how-it-works-btn"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('how_it_works');
                }}
                className={`py-1.5 transition-colors relative cursor-pointer ${
                  isActive('how_it_works')
                    ? 'text-blue-600 dark:text-blue-400 font-bold after:absolute after:bottom-[-20px] after:left-0 after:right-0 after:h-0.5 after:bg-blue-600 dark:after:bg-blue-400'
                    : 'hover:text-blue-600 dark:hover:text-blue-400'
                }`}
              >
                How It Works
              </a>

              <a
                href="/for-customers"
                id="nav-customer-guide-btn"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('customer_guide');
                }}
                className={`py-1.5 transition-colors relative cursor-pointer ${
                  isActive('customer_guide')
                    ? 'text-blue-600 dark:text-blue-400 font-bold after:absolute after:bottom-[-20px] after:left-0 after:right-0 after:h-0.5 after:bg-blue-600 dark:after:bg-blue-400'
                    : 'hover:text-blue-600 dark:hover:text-blue-400'
                }`}
              >
                For Customers
              </a>

              <a
                href="/become-a-professional"
                id="nav-become-pro-btn"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('become_pro');
                }}
                className={`py-1.5 transition-colors relative cursor-pointer ${
                  isActive('become_pro')
                    ? 'text-blue-600 dark:text-blue-400 font-bold after:absolute after:bottom-[-20px] after:left-0 after:right-0 after:h-0.5 after:bg-blue-600 dark:after:bg-blue-400'
                    : 'hover:text-blue-600 dark:hover:text-blue-400'
                }`}
              >
                Become a Professional
              </a>
            </nav>

            {/* RIGHT ACTION CONTROLS */}
            <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
              {/* Theme Toggle Icon */}
              <ThemeToggle />

              {/* Install PWA App (shown on sm+ in header; also available in mobile drawer & top banner) */}
              <div className="hidden sm:block">
                <InstallAppButton variant="navbar" />
              </div>

              {/* Quick 'Post a Job' Action for Desktop (Guests & Customers) */}
              {(!currentUser || currentUser.role === 'customer') && (
                <button
                  onClick={() => handleNavClick('post_job')}
                  className="hidden xl:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 border border-emerald-200 dark:border-emerald-800 transition-colors cursor-pointer"
                >
                  <PlusCircle className="w-3.5 h-3.5" />
                  <span>Post a Job Free</span>
                </button>
              )}

              {/* AUTHENTICATED USER MENU VS GUEST ACTIONS */}
              {currentUser ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    id="header-profile-menu-btn"
                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                    className="flex items-center gap-1.5 sm:gap-2 p-1 sm:p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors min-h-[40px] cursor-pointer border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
                    aria-label="User account menu"
                    aria-expanded={isRoleDropdownOpen}
                  >
                    <img
                      src={
                        currentUser.avatarUrl ||
                        'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80'
                      }
                      alt={currentUser.fullName}
                      className="w-8 h-8 rounded-full object-cover ring-2 ring-blue-600/30 dark:ring-blue-400/30"
                    />
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-100 hidden md:inline max-w-[110px] truncate">
                      {currentUser.fullName}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </button>

                  {/* Account Dropdown Menu */}
                  {isRoleDropdownOpen && (
                    <div
                      id="profile-dropdown-content"
                      className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-1.5rem)] bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 py-1.5 z-50 animate-in fade-in slide-in-from-top-2"
                    >
                      <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                        <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                          {currentUser.fullName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {currentUser.email}
                        </p>
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 capitalize">
                            {currentUser.role}
                          </span>
                          {currentUser.state && (
                            <span className="text-[11px] text-slate-400 dark:text-slate-500 truncate">
                              {currentUser.lga}, {currentUser.state}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="py-1">
                        {/* Theme Toggle within dropdown */}
                        <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                          <ThemeToggle variant="full" />
                        </div>

                        {currentUser.role === 'customer' && (
                          <>
                            <button
                              onClick={() => handleNavClick('customer_dashboard')}
                              className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 cursor-pointer"
                            >
                              <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              Customer Dashboard
                            </button>
                            <button
                              onClick={() => {
                                setIsRoleDropdownOpen(false);
                                navigateTo('post_job');
                              }}
                              className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 cursor-pointer"
                            >
                              <PlusCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                              Post a New Job
                            </button>
                            {currentUser.availableRoles?.includes('professional') ? (
                              <button
                                onClick={() => {
                                  setIsRoleDropdownOpen(false);
                                  switchActiveRole ? switchActiveRole('professional') : navigateTo('pro_dashboard');
                                }}
                                className="w-full text-left px-4 py-2.5 text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 flex items-center gap-2 cursor-pointer border-t border-slate-100 dark:border-slate-800"
                              >
                                <Briefcase className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                <span>Switch to Professional Portal</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setIsRoleDropdownOpen(false);
                                  navigateTo('pro_auth', { authMode: 'register' });
                                }}
                                className="w-full text-left px-4 py-2.5 text-xs font-bold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 flex items-center gap-2 cursor-pointer border-t border-slate-100 dark:border-slate-800"
                              >
                                <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                <span>Become a Professional</span>
                              </button>
                            )}
                          </>
                        )}

                        {currentUser.role === 'professional' && (
                          <>
                            <button
                              onClick={() => handleNavClick('pro_dashboard')}
                              className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 cursor-pointer"
                            >
                              <Building className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              Professional Dashboard
                            </button>
                            <button
                              onClick={() => {
                                setIsRoleDropdownOpen(false);
                                navigateTo('pro_profile', { proId: currentUser.id });
                              }}
                              className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 cursor-pointer"
                            >
                              <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              My Public Profile
                            </button>
                            {currentUser.availableRoles?.includes('customer') && (
                              <button
                                onClick={() => {
                                  setIsRoleDropdownOpen(false);
                                  switchActiveRole ? switchActiveRole('customer') : navigateTo('customer_dashboard');
                                }}
                                className="w-full text-left px-4 py-2.5 text-xs font-bold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 flex items-center gap-2 cursor-pointer border-t border-slate-100 dark:border-slate-800"
                              >
                                <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                <span>Switch to Customer Portal</span>
                              </button>
                            )}
                          </>
                        )}

                        {currentUser.role === 'admin' && (
                          <button
                            onClick={() => handleNavClick('admin_dashboard')}
                            className="w-full text-left px-4 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 cursor-pointer"
                          >
                            <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            Admin Control Panel
                          </button>
                        )}

                        <button
                          onClick={() => handleNavClick('messages')}
                          className="w-full text-left px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center gap-2 cursor-pointer"
                        >
                          <MessageSquare className="w-4 h-4 text-slate-400" />
                          Messages
                        </button>

                        <button
                          onClick={() => handleNavClick('notifications')}
                          className="w-full text-left px-4 py-2 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <Bell className="w-4 h-4 text-slate-400" />
                            <span>Notifications</span>
                          </div>
                          {unreadNotifsCount > 0 && (
                            <span className="w-4 h-4 bg-blue-600 text-white rounded-full text-[10px] font-bold flex items-center justify-center">
                              {unreadNotifsCount}
                            </span>
                          )}
                        </button>

                        <div className="border-t border-slate-100 dark:border-slate-800 my-1" />

                        <button
                          onClick={async () => {
                            setIsRoleDropdownOpen(false);
                            await logoutUser();
                          }}
                          className="w-full text-left px-4 py-2.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 flex items-center gap-2 cursor-pointer"
                        >
                          <LogOut className="w-4 h-4 text-rose-500 dark:text-rose-400" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* GUEST ACTION BUTTONS: Professional Blue Identity */
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button
                    id="header-sign-in-btn"
                    onClick={() => openAuthModal('login')}
                    className="px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Sign In
                  </button>
                  <button
                    id="header-sign-up-btn"
                    onClick={() => openAuthModal('register')}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 shadow-xs hover:shadow-md transition-all cursor-pointer whitespace-nowrap"
                  >
                    Sign Up
                  </button>
                </div>
              )}

              {/* MOBILE HAMBURGER TOGGLE */}
              <button
                id="mobile-menu-toggle-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 min-h-[42px] min-w-[42px] flex items-center justify-center rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                aria-label={isMobileMenuOpen ? 'Close Menu' : 'Open Menu'}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 3. MOBILE NAVIGATION DRAWER & BACKDROP */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 top-0 bg-slate-950/60 backdrop-blur-xs z-30 lg:hidden animate-in fade-in duration-200"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Drawer Content */}
          <div className="relative z-40 lg:hidden border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 pt-3 pb-6 space-y-3 shadow-2xl max-h-[calc(100dvh-96px)] overflow-y-auto animate-in slide-in-from-top-2 duration-200">
            {/* Authenticated User Status or Guest Sign In */}
            {currentUser ? (
              <div className="p-3.5 bg-slate-50 dark:bg-slate-800/80 rounded-2xl flex items-center justify-between border border-slate-200/70 dark:border-slate-700/60">
                <div className="flex items-center gap-3">
                  <img
                    src={
                      currentUser.avatarUrl ||
                      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80'
                    }
                    alt={currentUser.fullName}
                    className="w-10 h-10 rounded-xl object-cover ring-2 ring-blue-600/30"
                  />
                  <div>
                    <p className="text-xs font-bold text-slate-900 dark:text-white">
                      {currentUser.fullName}
                    </p>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 capitalize">
                      {currentUser.role} · {currentUser.lga || 'Nigeria'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    setIsMobileMenuOpen(false);
                    await logoutUser();
                  }}
                  className="text-xs text-rose-600 dark:text-rose-400 font-semibold px-2.5 py-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40 flex items-center gap-1 cursor-pointer"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  <span>Sign Out</span>
                </button>
              </div>
            ) : (
              <div className="p-3 bg-slate-50 dark:bg-slate-800/80 rounded-2xl flex items-center gap-2 border border-slate-200/70 dark:border-slate-700/60">
                <button
                  id="mobile-menu-sign-in-btn"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    openAuthModal('login');
                  }}
                  className="flex-1 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 rounded-xl text-xs font-bold text-center min-h-[44px] cursor-pointer"
                >
                  Sign In
                </button>
                <button
                  id="mobile-menu-sign-up-btn"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    openAuthModal('register');
                  }}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold text-center min-h-[44px] shadow-xs cursor-pointer"
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Quick Actions (Post Job & Become Pro) */}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button
                onClick={() => handleNavClick('post_job')}
                className="py-2.5 px-3 rounded-xl text-xs font-bold text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 min-h-[44px] flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <PlusCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span>Post a Job</span>
              </button>
              <button
                onClick={() => handleNavClick('become_pro')}
                className="py-2.5 px-3 rounded-xl text-xs font-bold text-blue-800 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 min-h-[44px] flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span>Become a Pro</span>
              </button>
            </div>

            {/* Main Navigation Items */}
            <div className="space-y-1 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs font-bold">
              {currentUser && (
                <>
                  {currentUser.role === 'customer' && (
                    <button
                      onClick={() => handleNavClick('customer_dashboard')}
                      className="w-full text-left py-2.5 px-3.5 rounded-xl text-blue-800 dark:text-blue-300 bg-blue-50/70 dark:bg-blue-950/40 min-h-[44px] flex items-center gap-2"
                    >
                      <User className="w-4 h-4" />
                      Customer Dashboard
                    </button>
                  )}
                  {currentUser.role === 'professional' && (
                    <button
                      onClick={() => handleNavClick('pro_dashboard')}
                      className="w-full text-left py-2.5 px-3.5 rounded-xl text-blue-800 dark:text-blue-300 bg-blue-50/70 dark:bg-blue-950/40 min-h-[44px] flex items-center gap-2"
                    >
                      <Building className="w-4 h-4" />
                      Professional Dashboard
                    </button>
                  )}
                  {currentUser.role === 'admin' && (
                    <button
                      onClick={() => handleNavClick('admin_dashboard')}
                      className="w-full text-left py-2.5 px-3.5 rounded-xl text-blue-800 dark:text-blue-300 bg-blue-50/70 dark:bg-blue-950/40 min-h-[44px] flex items-center gap-2"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Admin Control Panel
                    </button>
                  )}
                </>
              )}

              <button
                onClick={() => handleNavClick('find_pro')}
                className="w-full text-left py-2.5 px-3.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 min-h-[44px] flex items-center"
              >
                Find a Professional
              </button>
              <button
                onClick={() => handleNavClick('how_it_works')}
                className="w-full text-left py-2.5 px-3.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 min-h-[44px] flex items-center"
              >
                How It Works
              </button>
              <button
                onClick={() => handleNavClick('customer_guide')}
                className="w-full text-left py-2.5 px-3.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 min-h-[44px] flex items-center"
              >
                For Customers
              </button>
              <button
                onClick={() => handleNavClick('verification')}
                className="w-full text-left py-2.5 px-3.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 min-h-[44px] flex items-center"
              >
                Trust & Verification
              </button>
              <button
                onClick={() => handleNavClick('faq')}
                className="w-full text-left py-2.5 px-3.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 min-h-[44px] flex items-center"
              >
                <HelpCircle className="w-4 h-4 mr-2 text-slate-400" />
                Frequently Asked Questions
              </button>
              <button
                onClick={() => handleNavClick('contact')}
                className="w-full text-left py-2.5 px-3.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 min-h-[44px] flex items-center"
              >
                <PhoneCall className="w-4 h-4 mr-2 text-slate-400" />
                Contact Support
              </button>
            </div>

            {/* Theme & PWA Controls in Mobile Menu */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
              <ThemeToggle variant="full" />
              <InstallAppButton
                variant="mobile-drawer"
                onAfterClick={() => setIsMobileMenuOpen(false)}
              />
            </div>
          </div>
        </>
      )}
    </header>
  );
};
